import { DEFAULT_CURRENCY } from '@/api/rest/resources/types/currency';

import { useDefaultCurrency } from './useDefaultCurrency';

/**
 * Displays currency values.
 * Rules:
 * - If number has 0 to 3 integer digits, 2 decimal digits are shown
 * - If number has 4 to 5 integer digits, 1 decimal digits are shown
 * - If number has 6 or more integer digits, no decimal digits are shown
 * - Decimal digits are always trimmed without rounding
 *
 * @param input - The numeric value to format
 * @param currency - Optional currency code (defaults to organization's currency if not provided)
 * @param options - Optional Intl.NumberFormatOptions
 * @returns Formatted currency string
 */
export const useDisplayCurrency = (
  input: number | string,
  currencyArg?: string,
  options?: Intl.NumberFormatOptions,
) => {
  const defaultCurrency = useDefaultCurrency();
  const currency = currencyArg ?? defaultCurrency;

  return getDisplayCurrency(input, window.navigator.language, currency, options);
};

/**
 * Displays currency values.
 * Rules:
 * - If number has 0 to 3 integer digits, 2 decimal digits are shown
 * - If number has 4 to 5 integer digits, 1 decimal digits are shown
 * - If number has 6 or more integer digits, no decimal digits are shown
 * - Decimal digits are always trimmed without rounding
 *
 * @param input - The numeric value to format
 * @param currency - Optional currency code (defaults to DEFAULT_CURRENCY if not provided)
 * @param options - Optional Intl.NumberFormatOptions
 * @returns Formatted currency string
 */
export const getDisplayCurrency = (
  input: number | string,
  language: string,
  currency?: string,
  options?: Intl.NumberFormatOptions,
) => {
  const number = Number(input);
  if (Number.isNaN(number)) throw Error(`Invalid number received: ${input}`);

  const numberAbs = Math.abs(number);

  const formatCurrency = (valueInput: number, minFractionDigits: number, maxFractionDigits: number) => {
    const fractionDigits = {
      max: options?.maximumFractionDigits ?? maxFractionDigits,
      min: options?.minimumFractionDigits ?? minFractionDigits,
    };
    const parts = valueInput.toString().split('.');
    const integer = parts[0];
    const fraction = parts[1]?.substring(0, fractionDigits.max) ?? '0';
    const unroundedNumber = Number(`${integer}.${fraction}`);

    return new Intl.NumberFormat(language, {
      style: 'currency',
      currency: currency ?? DEFAULT_CURRENCY,
      minimumFractionDigits: fractionDigits.min,
      maximumFractionDigits: fractionDigits.max,
      ...options,
    })
      .formatToParts(unroundedNumber)
      .map(({ type, value }) => {
        if (type === 'group') {
          return `\u2009`;
        }
        return value;
      })
      .reduce((string, part) => string + part);
  };

  let fractionDigits = 0;

  if (numberAbs < 1_000) {
    fractionDigits = 2;
  } else if (numberAbs < 100_000) {
    fractionDigits = 1;
  }

  if (Math.abs(number) >= 1_000_000_000) {
    const billionValue = number / 1_000_000_000;
    const formattedBillion = formatCurrency(billionValue, billionValue % 1 === 0 ? 0 : 1, 3);
    return `${formattedBillion}B`;
  }

  return formatCurrency(number, fractionDigits, fractionDigits);
};
